import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { FALLBACK_STARTER_PROMPTS } from '../constants';
import { useChatBlkContext } from '../providers';
import { ChatBlkThunkDispatch, sendQuery } from '../store/store';
import { QueryTypeEnum } from '../types';
import { uuidv4 } from '../utils';

/**
 * Custom hook to retrieve and split starter prompts for the chat interface.
 *
 * This hook attempts to extract prompt data from a custom web component (`blk-cwp-component`)
 * by reading its `header-data` attribute, parsing it, and extracting the `labelFrontDoorPrompts`.
 * If the component or data is unavailable, it falls back to a predefined set of prompts.
 *
 * The prompts are split into two halves (`topHalf` and `bottomHalf`), each containing
 * objects with a `displayValue` property for rendering purposes.
 *
 * @returns An object containing:
 * - `topHalf`: Array of prompt objects for the top section.
 * - `bottomHalf`: Array of prompt objects for the bottom section.
 */
const useStarterPrompts = () => {
    const dispatch = useDispatch<ChatBlkThunkDispatch>();
    const collection = document.getElementsByTagName('blk-cwp-component');
    const { promptBoxRef, chatCanvasRef } = useChatBlkContext();
    const headerData = collection?.[0]?.getAttribute('header-data');
    const { labelFrontDoorPrompts = [] } = headerData
        ? JSON.parse(headerData)?.chatBlk || {}
        : {};
    const [scrollVelocity, setScrollVelocity] = useState<number>(4);
    useEffect(() => {
        /**
         * The speed of the scrolling pills has decreased to be closer to:
         * Mobile (< 768px): 3 pixels per second
         * Tablet (768px - 1024px): 4 pixels per second
         * Desktop (≥ 1024px): 6 pixels per second
         */
        const handleResize = () => {
            const width =
                chatCanvasRef.current?.getBoundingClientRect().width || 0;
            if (width < 768) {
                setScrollVelocity(3);
            } else if (width >= 768 && width < 1024) {
                setScrollVelocity(4);
            } else {
                setScrollVelocity(6);
            }
        };

        chatCanvasRef?.current?.addEventListener(
            'embeddedPanelResizeEnd',
            handleResize
        );
        return () => {
            chatCanvasRef?.current?.removeEventListener(
                'embeddedPanelResizeEnd',
                handleResize
            );
        };
    }, [chatCanvasRef]);

    const starterPrompts: Array<string> = labelFrontDoorPrompts.length
        ? labelFrontDoorPrompts.replace(/&#39;/g, "'").split('|')
        : FALLBACK_STARTER_PROMPTS;

    const topHalf = useMemo(
        () =>
            starterPrompts
                .slice(0, starterPrompts.length / 2)
                .map((prompt: string) => ({ displayValue: prompt })),
        []
    );
    const bottomHalf = useMemo(
        () =>
            starterPrompts
                .slice(starterPrompts.length / 2)
                .map((prompt: string) => ({ displayValue: prompt })),
        []
    );

    const handlePromptSelected = async (event: CustomEvent) => {
        await promptBoxRef.current?.setValue?.('');
        const { context } = event.detail;
        dispatch(
            sendQuery({
                prompt: (
                    context as object & {
                        displayValue: string;
                    }
                ).displayValue,
                queryType: QueryTypeEnum.QUERY,
                chatExchangeId: uuidv4(),
                hashId: uuidv4(),
            })
        );
    };

    return {
        topHalf,
        bottomHalf,
        scrollVelocity,
        handlePromptSelected,
    };
};

export default useStarterPrompts;
